import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Store,
  Camera,
  MapPin,
  Clock,
  CreditCard,
  Truck,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Upload,
  Mail,
  Phone,
  Globe,
  BadgeCheck,
  Edit,
  ArrowRight
} from 'lucide-react'

interface StoreProfile {
  id: string
  name: string
  description: string | null
  address: string | null
  opening_hours: string | null
  payment_methods: string[] | null
  delivery_terms: string | null
  return_policy: string | null
  logo_url: string | null
  contact_email: string | null
  contact_phone: string | null
  website: string | null
}

export function StoreProfilePage() {
  const navigate = useNavigate()
  const user = useAuthStore(state => state.user)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [store, setStore] = useState<StoreProfile | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    opening_hours: '',
    payment_methods: ['cash'],
    delivery_terms: '',
    return_policy: '',
    contact_email: '',
    contact_phone: '',
    website: ''
  })
  
  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Espèces' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'credit_card', label: 'Carte bancaire' },
    { value: 'bank_transfer', label: 'Virement bancaire' },
    { value: 'moncash', label: 'MonCash' },
  ]

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    fetchStoreProfile()
  }, [user, navigate])

  const fetchStoreProfile = async () => {
    try {
      setLoading(true)
      
      // Fetch the store profile from the database
      const { data: storeData, error: storeError } = await supabase
        .from('seller_stores')
        .select('*')
        .eq('seller_id', user?.id)
        .single()
        
      if (storeError) {
        if (storeError.code === 'PGRST116') {
          // No store found
          navigate('/seller/register')
          return
        }
        throw storeError
      }
      
      // Set the store data
      setStore(storeData)
      
      // Set the form data
      setFormData({
        name: storeData.name || '',
        description: storeData.description || '',
        address: storeData.address || '',
        opening_hours: storeData.opening_hours || '',
        payment_methods: storeData.payment_methods || ['cash'],
        delivery_terms: storeData.delivery_terms || '',
        return_policy: storeData.return_policy || '',
        contact_email: storeData.contact_email || user?.email || '',
        contact_phone: storeData.contact_phone || user?.phone_number || '',
        website: storeData.website || ''
      })
      
      // Set the logo preview
      if (storeData.logo_url) {
        setLogoPreview(storeData.logo_url)
      }
    } catch (err) {
      console.error("Error fetching store profile:", err)
      setError("Impossible de charger le profil du magasin.")
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Le fichier est trop volumineux. Taille maximum : 5 Mo.")
        return
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        setError("Le fichier doit être une image.")
        return
      }
      
      // Set the file
      setLogoFile(file)
      
      // Create a preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handlePaymentMethodToggle = (method: string) => {
    setFormData(prev => {
      const methods = [...prev.payment_methods]
      if (methods.includes(method)) {
        return { ...prev, payment_methods: methods.filter(m => m !== method) }
      } else {
        return { ...prev, payment_methods: [...methods, method] }
      }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user || !store) return
    
    try {
      setSaving(true)
      setError(null)
      setShowToast(false)
      
      let logoUrl = store.logo_url
      
      // If a new logo was uploaded, save it
      if (logoFile) {
        const fileExt = logoFile.name.split('.').pop()
        const fileName = `${user.id}/${Date.now()}.${fileExt}`
        
        // Upload to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('store-logos')
          .upload(fileName, logoFile, { upsert: true })
          
        if (uploadError) throw uploadError
        
        // Get the public URL
        const { data: { publicUrl } } = supabase.storage
          .from('store-logos')
          .getPublicUrl(fileName)
          
        logoUrl = publicUrl
      }
      
      // Update the store profile
      const { error: updateError } = await supabase
        .from('seller_stores')
        .update({
          name: formData.name,
          description: formData.description,
          address: formData.address,
          opening_hours: formData.opening_hours,
          payment_methods: formData.payment_methods,
          delivery_terms: formData.delivery_terms,
          return_policy: formData.return_policy,
          logo_url: logoUrl,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          website: formData.website
        })
        .eq('id', store.id)
        
      if (updateError) throw updateError
      
      // Show success message
      setToastMessage('Profil mis à jour avec succès')
      setToastType('success')
      setShowToast(true)
      
      // Refresh the store profile
      fetchStoreProfile()
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
      
    } catch (err) {
      console.error("Error updating store profile:", err)
      setError("Échec de la mise à jour du profil.")
      setToastMessage("Échec de la mise à jour du profil.")
      setToastType('error')
      setShowToast(true)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}
      
      <div className="max-w-5xl mx-auto">
        <div className="mb-6">
          <BackButton to="/seller/dashboard" label="Retour au Tableau de Bord" />
        </div>
        
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-primary to-blue-600 px-6 py-8 text-white">
            <h1 className="text-2xl font-bold">Profil de la Boutique</h1>
            <p className="mt-2 text-white/80">
              Mettez à jour et gérez les informations publiques de votre boutique
            </p>
          </div>
          
          <div className="p-6">
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {/* Store Logo */}
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo / Image de la Boutique
                  </label>
                  <div className="flex items-center">
                    <div className="relative h-32 w-32 rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-300 flex justify-center items-center">
                      {logoPreview ? (
                        <img 
                          src={logoPreview} 
                          alt="Logo de la Boutique" 
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <Store className="h-12 w-12 text-gray-400" />
                      )}
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        className="sr-only"
                        accept="image/*"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute inset-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                      >
                        <Camera className="h-6 w-6 text-white" />
                      </button>
                    </div>
                    <div className="ml-5">
                      <button
                        type="button"
                        className="bg-white py-2 px-3 border-2 border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Changer le Logo
                      </button>
                      <p className="mt-2 text-xs text-gray-500">
                        Uploadez une image carrée pour de meilleurs résultats. Max 5 Mo.
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Store Name */}
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Nom de la Boutique *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1 block w-full border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
                
                {/* Store Description */}
                <div className="sm:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Description de la Boutique
                  </label>
                  <textarea
                    id="description"
                    rows={5}
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="mt-1 block w-full border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Décrivez votre boutique, vos produits et ce qui vous rend unique..."
                  />
                </div>
                
                {/* Contact Information */}
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Informations de Contact</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700">
                        Email de Contact
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Mail className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="email"
                          id="contact_email"
                          value={formData.contact_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                          className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="contact_phone" className="block text-sm font-medium text-gray-700">
                        Téléphone de Contact
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Phone className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          id="contact_phone"
                          value={formData.contact_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                          className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="website" className="block text-sm font-medium text-gray-700">
                        Site Web (Optionnel)
                      </label>
                      <div className="mt-1 relative rounded-md shadow-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Globe className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="url"
                          id="website"
                          value={formData.website}
                          onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                          className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                          placeholder="https://exemple.com"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Physical Store Information */}
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Informations Boutique Physique</h3>
                  
                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                      Adresse de la Boutique (si applicable)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MapPin className="h-4 w-4 text-gray-400" />
                      </div>
                      <textarea
                        id="address"
                        rows={3}
                        value={formData.address}
                        onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Rue, Ville, État/Province, Code postal"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="opening_hours" className="block text-sm font-medium text-gray-700">
                      Horaires d'Ouverture (si applicable)
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Clock className="h-4 w-4 text-gray-400" />
                      </div>
                      <textarea
                        id="opening_hours"
                        rows={3}
                        value={formData.opening_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, opening_hours: e.target.value }))}
                        className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Lundi-Vendredi: 9h-17h, Samedi: 10h-16h, Dimanche: Fermé"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Payment Methods */}
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Moyens de Paiement</h3>
                  <p className="text-sm text-gray-500 mb-4">Sélectionnez tous les moyens de paiement que votre boutique accepte</p>
                  
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                    {PAYMENT_METHODS.map(method => (
                      <div key={method.value} className="relative flex items-start">
                        <div className="flex items-center h-5">
                          <input
                            id={`payment-${method.value}`}
                            name="payment_methods"
                            type="checkbox"
                            checked={formData.payment_methods.includes(method.value)}
                            onChange={() => handlePaymentMethodToggle(method.value)}
                            className="focus:ring-primary h-4 w-4 text-primary border-2 border-gray-300 rounded"
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label htmlFor={`payment-${method.value}`} className="font-medium text-gray-700">
                            {method.label}
                          </label>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Delivery & Return Policies */}
                <div className="sm:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Conditions de Livraison & Retour</h3>
                  
                  <div>
                    <label htmlFor="delivery_terms" className="block text-sm font-medium text-gray-700">
                      Conditions de Livraison
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Truck className="h-4 w-4 text-gray-400" />
                      </div>
                      <textarea
                        id="delivery_terms"
                        rows={3}
                        value={formData.delivery_terms}
                        onChange={(e) => setFormData(prev => ({ ...prev, delivery_terms: e.target.value }))}
                        className="block w-full pl-10 border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                        placeholder="Décrivez vos options de livraison, délais et frais applicables..."
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <label htmlFor="return_policy" className="block text-sm font-medium text-gray-700">
                      Politique de Retour
                    </label>
                    <textarea
                      id="return_policy"
                      rows={3}
                      value={formData.return_policy}
                      onChange={(e) => setFormData(prev => ({ ...prev, return_policy: e.target.value }))}
                      className="mt-1 block w-full border-2 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                      placeholder="Décrivez votre politique de retour, conditions, délais et processus..."
                    />
                  </div>
                </div>
              </div>
              
              {/* Submit Button */}
              <div className="mt-8 flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement en cours...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Enregistrer les Modifications
                    </>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
        
        {/* Store Preview Section */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Aperçu de la Boutique</h2>
            <p className="mt-1 text-sm text-gray-500">
              Voici comment votre boutique apparaîtra aux clients
            </p>
          </div>
          
          <div className="p-6">
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Logo de la Boutique" 
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Store className="h-8 w-8 text-gray-400 m-4" />
                  )}
                </div>
                
                <div className="ml-4">
                  <div className="flex items-center">
                    <h3 className="text-lg font-bold text-gray-900">{formData.name || "Nom de Votre Boutique"}</h3>
                    <BadgeCheck className="ml-2 h-5 w-5 text-blue-500" />
                  </div>
                  <div className="mt-1 flex items-center text-sm text-gray-500">
                    <MapPin className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                    <span>{formData.address || "Adresse de la boutique"}</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-sm text-gray-700">
                <p>{formData.description || "La description de votre boutique apparaîtra ici."}</p>
              </div>
              
              <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Informations de Contact</h4>
                  <ul className="mt-2 text-sm text-gray-600 space-y-1">
                    <li className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      {formData.contact_email || "email@exemple.com"}
                    </li>
                    <li className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      {formData.contact_phone || "Numéro de téléphone"}
                    </li>
                    {formData.website && (
                      <li className="flex items-center">
                        <Globe className="h-4 w-4 text-gray-400 mr-2" />
                        {formData.website}
                      </li>
                    )}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Horaires d'Ouverture</h4>
                  <p className="mt-2 text-sm text-gray-600">
                    {formData.opening_hours || "Vos horaires d'ouverture apparaîtront ici."}
                  </p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Moyens de Paiement</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.payment_methods.map(method => (
                      <span key={method} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <CreditCard className="mr-1 h-3 w-3" />
                        {PAYMENT_METHODS.find(m => m.value === method)?.label || method}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Conditions de Livraison</h4>
                  <p className="mt-2 text-sm text-gray-600">
                    {formData.delivery_terms || "Vos conditions de livraison apparaîtront ici."}
                  </p>
                </div>
              </div>
              
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-900">Politique de Retour</h4>
                <p className="mt-2 text-sm text-gray-600">
                  {formData.return_policy || "Votre politique de retour apparaîtra ici."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}